import React, { useEffect, useState } from 'react';

import { Home } from '@mui/icons-material';
import { Spinner } from 'react-bootstrap';
import { FaDownload } from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { downloadEvaluationPdf } from './downloadEvaluationPdf';
import { evalFileName, mergeAudits, parseExceptions, qualListCell } from './helpers';
import { getDqfAudits, getDqfHeader, startDqfAudit, subscribeDqfAudits } from './operations';
import { Breadcrumbs, Button, Footer, SubHeader } from '../../../components';
import { formatIsoToDate } from '../../../utils';
import './styles.scoped.css';

// CP-836 slice 2 — AI Review page (mockup p.28). Displays the DqfAudit evaluation records for a DQF
// (newest first) + a reading pane for the selected evaluation. Live-updates via onUpdateDqfAudit.
// CP-854 added the per-row and per-pane PDF exports.

// CP-867 — how often to re-read the list while an evaluation is in flight, and how long to keep
// doing it. The limit is well clear of the worker's 180s hard cap, so reaching it means the
// evaluation is stuck rather than slow.
const POLL_INTERVAL_MS = 10000;
const POLL_LIMIT_MS = 240000;

const STALLED_MESSAGE = 'This evaluation has not reported back. Refresh the page to check again, or run it once more.';

const AIReview = () => {
  const navigation = useNavigate();
  const { id } = useParams();

  const [header, setHeader] = useState(null);
  const [audits, setAudits] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [pdfError, setPdfError] = useState('');
  const [stalled, setStalled] = useState(false);
  // Bumped when the browser comes back from an outage, to re-arm polling. `processing` does not
  // change at that moment, so without this the polling effect never re-runs and a stalled screen
  // stays stalled — see the wake-up effect below.
  const [pollEpoch, setPollEpoch] = useState(0);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    // Arrived via Evaluate-to-Send? Kick off the audit HERE — after AI Review's own verifyRoute
    // (Private gates this screen), so verifyRoute stays the first GraphQL on the screen. Clear the
    // history flag so a manual refresh doesn't start another evaluation.
    const startAudit = location.state?.startAudit === true;
    if (startAudit) window.history.replaceState({}, '');
    (async () => {
      const [head, list] = await Promise.all([getDqfHeader(id), getDqfAudits(id)]);
      if (cancelled) return;
      setHeader(head);
      setAudits(list);
      setSelectedId(list[0]?.id || null);
      setLoading(false);
      if (startAudit) {
        const started = await startDqfAudit(id);
        if (cancelled || !started?.id) return;
        setAudits(prev => [started, ...prev.filter(a => a.id !== started.id)]);
        setSelectedId(started.id);
      }
    })();

    const sub = subscribeDqfAudits(
      id,
      updated => setAudits(prev => mergeAudits(prev, [updated])),
      // On reconnect, re-read the list: a completion that fired while the socket was down was never
      // delivered and would otherwise sit on screen as PROCESSING forever.
      async () => {
        const list = await getDqfAudits(id);
        if (!cancelled) setAudits(prev => mergeAudits(prev, list));
      },
    );

    return () => {
      cancelled = true;
      sub?.unsubscribe?.();
    };
    // location.state is intentionally read once on mount / id change (not a dependency).
  }, [id]);

  const selected = audits.find(a => a.id === selectedId) || null;
  const processing = audits.some(a => a.status === 'PROCESSING');

  // CP-867 — polling safety net, and the part that actually guarantees this screen converges.
  //
  // The subscription is the primary path and reconnecting improves it, but neither can promise
  // delivery: a completion that fires inside a disconnect gap is simply never sent. Re-reading the
  // list while an evaluation is in flight closes every one of those holes at once, whatever caused
  // them. A UAT tester lost an evening on 2026-09-21 to a spinner that outlived the evaluation by
  // several minutes, re-running files that had already finished.
  //
  // Bounded deliberately. The worker is hard-capped at 180s, so past POLL_LIMIT_MS a record is not
  // slow — it is stuck, and a worker that dies before writing its result leaves PROCESSING set for
  // good. At that point stop polling and say so, because a screen that gives up honestly is more
  // use than one that spins hopefully.
  useEffect(() => {
    if (!processing) return undefined;
    setStalled(false); // a fresh evaluation is in flight — clear any previous stall
    let cancelled = false;
    let elapsed = 0;
    const timer = setInterval(async () => {
      elapsed += POLL_INTERVAL_MS;
      const list = await getDqfAudits(id);
      if (cancelled) return;
      setAudits(prev => mergeAudits(prev, list));
      if (elapsed >= POLL_LIMIT_MS) {
        clearInterval(timer);
        setStalled(true);
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [processing, id, pollEpoch]);

  // CP-867 follow-up — recover from an outage on a signal we actually control.
  //
  // The reconnect handling in subscribeDqfAudits assumed a dropped socket surfaces as an `error`
  // on the observable. It does not: Amplify's AppSync client retries the socket internally and
  // never tells the observer. Proven by a 4-minute cable-pull on 2026-09-22 — the HAR shows zero
  // WebSocket attempts for the whole outage, then two successful reconnects afterwards with no
  // accompanying re-fetch, because our onReconnect callback was never invoked.
  //
  // Two consequences, and this effect is what covers both. Past the poll bound nothing was left
  // running at all, so the screen sat on "has not reported back" until a manual refresh. And even
  // a healthy socket could not have rescued it: onUpdateDqfAudit only pushes NEW mutations, so a
  // completion that happened during the outage is never re-sent. Only a re-read recovers it.
  //
  // visibilitychange matters more than online here. A pulled cable fires `online`, but a laptop
  // waking from sleep frequently does not — the browser may never have considered itself offline —
  // and sleep is the likelier real-world cause. The tab does become visible again either way.
  //
  // Guarded on `processing` so an ordinary tab switch costs nothing when no evaluation is pending.
  useEffect(() => {
    if (!processing) return undefined;
    const wake = async () => {
      if (document.visibilityState === 'hidden') return;
      const list = await getDqfAudits(id);
      setAudits(prev => mergeAudits(prev, list));
      // Re-arm the poll: if that read showed the evaluation still running, a fresh budget starts
      // and the stall clears; if it completed, `processing` goes false and the effect unmounts.
      setPollEpoch(n => n + 1);
    };
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', wake);
    return () => {
      window.removeEventListener('online', wake);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [processing, id]);

  // CP-854 — download the evaluation report. `downloadingId` is a reentrancy guard: rendering is
  // synchronous work on the main thread, so every download button goes disabled while one runs
  // rather than looking pressable and silently no-op'ing. The remaining disabled cases are
  // genuine no-content ones (a PROCESSING audit, or nothing selected in the pane), not input
  // gating.
  const handleDownload = async audit => {
    if (!audit || downloadingId) return;
    setDownloadingId(audit.id);
    try {
      await downloadEvaluationPdf(audit, header, id);
      setPdfError('');
    } catch (e) {
      setPdfError('Could not generate the PDF. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const renderPane = () => {
    if (!selected) return <p className="pane-empty">No evaluations yet for this file.</p>;
    if (selected.status === 'PROCESSING') {
      return stalled ? (
        <p className="pane-status error">{STALLED_MESSAGE}</p>
      ) : (
        <p className="pane-status">
          <Spinner animation="border" size="sm" /> Evaluation in progress…
        </p>
      );
    }
    if (selected.status === 'ERROR') {
      return (
        <p className="pane-status error">
          {selected.resultNote || 'System Error, could not evaluate'}
          {selected.errorMessage ? ` — ${selected.errorMessage}` : ''}
        </p>
      );
    }
    const exceptions = parseExceptions(selected);
    if (!exceptions.length) return <p className="no-issues">No Issues found.</p>;
    return (
      <ol className="exceptions">
        {exceptions.map(ex => (
          <li key={`${ex.number}-${ex.title}`} className="exception">
            <p className="exception-title">{ex.title}</p>
            <p>
              <span className="exception-label">Rule:</span> {ex.rule}
            </p>
            <p>
              <span className="exception-label">Evidence:</span> {ex.evidence}
            </p>
            <p>
              <span className="exception-label">Severity:</span> {ex.severity}
            </p>
            <p>
              <span className="exception-label">Page:</span> {ex.page}
            </p>
          </li>
        ))}
      </ol>
    );
  };

  const info = [
    ['File #', header?.id || id],
    ['Client', header?.name],
    ['Driver', header?.driverName],
    ['Status', header?.status],
    ['DQF Specialist', header?.dqSpecialistUser],
    ['Created', header?.createdAt ? formatIsoToDate(header.createdAt) : ''],
    ['Branch', header?.branch],
    ['Tier', header?.tierDescription || header?.tier],
  ];

  return (
    <>
      <SubHeader
        text="Centerline Driver Qualification"
        buttons={[
          { icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigation('/driver-qualification') },
        ]}
      />
      <Breadcrumbs
        leftTitle="DQF Home"
        middleTitle={`File ${id}`}
        midItemAction={() => navigation(`/driver-qualification/corporatedq/${id}`)}
        rightTitle="AI Review"
        route="/driver-qualification"
      />
      <div className="wrap">
        <div className="box">
          <div className="box-header">AI Review</div>

          <div className="info-grid">
            {info.map(([label, value]) => (
              <div className="info-item" key={label}>
                <span className="info-label">{label}</span>
                <span className="info-value">{value ?? ''}</span>
              </div>
            ))}
          </div>

          <p className="section-label">Evaluation Files</p>
          {processing &&
            (stalled ? (
              <div className="processing stalled">
                <span>{STALLED_MESSAGE}</span>
              </div>
            ) : (
              <div className="processing">
                <Spinner animation="border" size="sm" />{' '}
                <span>An evaluation is running — this list updates when it completes.</span>
              </div>
            ))}
          {loading ? (
            <div className="processing">
              <Spinner animation="border" size="sm" /> <span>Loading…</span>
            </div>
          ) : (
            <table className="eval-table">
              <thead>
                <tr>
                  <th>Time / Date</th>
                  <th>File</th>
                  <th>Note</th>
                  <th>Qual List Available</th>
                  <th>Download</th>
                </tr>
              </thead>
              <tbody>
                {audits.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="no-data">
                      No Data
                    </td>
                  </tr>
                ) : (
                  audits.map(audit => (
                    <tr key={audit.id} className={audit.id === selectedId ? 'selected' : ''}>
                      <td>{audit.createdAt ? formatIsoToDate(audit.createdAt) : ''}</td>
                      <td>
                        <button type="button" className="file-link" onClick={() => setSelectedId(audit.id)}>
                          {evalFileName(audit)}
                        </button>
                      </td>
                      <td>{audit.resultNote || 'No Data'}</td>
                      <td>{qualListCell(audit)}</td>
                      <td className="row-actions">
                        <Button
                          variant="text"
                          icon={<FaDownload />}
                          aria-label={`Download PDF for evaluation ${evalFileName(audit)}`}
                          disabled={audit.status === 'PROCESSING' || !!downloadingId}
                          onPress={() => handleDownload(audit)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          <div className="section-head">
            <p className="section-label">Evaluation</p>
            <Button
              variant="text"
              icon={<FaDownload />}
              text="PDF"
              aria-label="Download PDF of the evaluation shown below"
              disabled={!selected || selected.status === 'PROCESSING' || !!downloadingId}
              onPress={() => handleDownload(selected)}
            />
          </div>
          {pdfError && <div className="pdf-error">{pdfError}</div>}
          <div className="pane">{renderPane()}</div>
        </div>

        <div className="actions">
          <Button variant="outline" onPress={() => navigation(`/driver-qualification/corporatedq/${id}`)}>
            Return to DQF to Correct
          </Button>
        </div>
      </div>
      <Footer />
    </>
  );
};
export default AIReview;
